import {
  query,
  where,
  collection,
  onSnapshot,
  Unsubscribe,
  updateDoc,
  doc,
  runTransaction,
} from "firebase/firestore";
import { ref, uploadBytes } from "firebase/storage";
import type { User } from "firebase/auth";
import { db, auth, storage } from "@/Utils/firebase";
import {
  RotationManager,
  RotationEvents,
  RotationEventData,
} from "./RotationManager";
import { logError, logInfo } from "@/Utils/logger";
import { ensureAuthedUser } from "@/Utils/ensureAuthedUser";
import {
  backgroundProcessingEndpoint,
  isProdEnv,
  uploadPhotosEndpoint,
  SCAN_HEARTBEAT_KEY,
  SCAN_HEARTBEAT_INTERVAL_MS,
} from "@/Utils/constants";
import { markScanSubmitted } from "@/Utils/scanSubmittedMarker";
import { v4 as uuid } from "uuid";
import { ScanModel } from "./ScanModel";

export type UploadEventMap = {
  uploadError: undefined | Error;
  uploadProgress: number;
  uploadSuccess: undefined;
  uploadCompleted: undefined;
  uploadReset: undefined;
  debugUpload: {
    debugScanId: string;
    isDebugUpload: boolean;
  };
  discarded: undefined;
};

export const UploadEvents = {
  ERROR: "uploadError",
  PROGRESS: "uploadProgress",
  SUCCESS: "uploadSuccess",
  COMPLETED: "uploadCompleted",
  RESET: "uploadReset",
  DEBUG_UPLOAD: "debugUpload",
  DISCARDED: "discarded",
} as const;

const MAX_UPLOAD_RETRIES = 3;
const RETRY_BASE_DELAY_MS = 5000; // 5s, 10s, 20s with exponential backoff

/**
 * How long an upload may sit with no completion before we call it stalled.
 *
 * `uploadCompleted` flips only when the `upload_processing` listener observes
 * `uploaded: true`. If /bin-predict succeeded but that never arrives — the
 * backend stalls part-way through the 22 images, or the onSnapshot listener
 * dies on a connection blip and never re-establishes — then nothing throws,
 * no ERROR is emitted, finalizeScan is never reached, and without this the
 * user waits on "Submitting scan ..." forever. Note this is NOT the same as a
 * failing upload: exhausted retries already surface an error (see the catch in
 * upload()). This covers the silent case.
 *
 * Sized from production, not guessed. Measured `updated_timestamp -
 * created_timestamp` on 836 prod `upload_processing` docs with
 * `uploaded: true` — that span is exactly this wait, since the backend bumps
 * `updated_timestamp` on every image it processes and sets `uploaded` when the
 * last one lands:
 *
 *     p50 68s · p90 88s · p95 97s · p99 120s · max 166s · none over 300s
 *
 * 5 minutes is ~1.8x the worst real scan and ~2.5x p99, with room left for the
 * frame-upload leg (which happens before `created_timestamp` and so isn't in
 * those numbers) plus the retry ladder's 35s of backoff. It also sits well
 * inside the backend's own SUBMITTING_TTL_SECONDS (10 min), so a scan this
 * gives up on is one the server would abandon too.
 */
const UPLOAD_STALL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * Minimum runway left on the stall watchdog after the tab is foregrounded.
 * A suspended page can return with its deadline already expired while a
 * `uploaded: true` snapshot is still queued for delivery; firing in that
 * window would throw away 22 good frames. Cheap insurance against a
 * false positive whose cost is a destroyed scan.
 */
const STALL_FOREGROUND_GRACE_MS = 30 * 1000;

/**
 * Hard per-request bound on /upload-photos. BUG-0007 2a.
 *
 * The foreground handler used to abort this request on every
 * `visibilitychange`, which is what bounded a suspended upload. That abort was
 * doing far more harm than good — prod logged 253 POSTs, 253 `saved 22
 * originals`, zero 5xx across 24 days, and one measured abort landed 6.7s
 * AFTER the server had fully succeeded, then queued a ~6MB re-upload of photos
 * GCS already held. Removing it without a replacement would reintroduce the
 * unbounded hang it was there to prevent, so the bound moves here.
 *
 * 60s against a 38.9s worst observed server-side save (p50 4.0s) — comfortably
 * above the real tail, and well inside both the finalize watchdog
 * (FINALIZE_STALL_TIMEOUT_MS) and the backend's 10-minute
 * SUBMITTING_TTL_SECONDS.
 */
const UPLOAD_PHOTOS_TIMEOUT_MS = 60 * 1000;

/**
 * Pause before retrying a request we cancelled ourselves on foreground.
 * BUG-0007 2c.
 *
 * "Retry immediately" fires at the exact moment the network is least likely to
 * be back: the radio has just woken with the tab. On 2026-08-03 the immediate
 * retry failed 1.6s later with `Load failed`, and that scan's photos never
 * reached the server at all — the only one of four observed where nothing
 * arrived. Short enough to be invisible, long enough for the link to settle.
 */
const FOREGROUND_RETRY_GRACE_MS = 1500;

/**
 * How long the whole finalize sequence may run before we stop waiting. BUG-0007
 * Phase 3.
 *
 * Every existing guard is structurally blind to a hang here: onUploadStalled()
 * returns early on `uploadCompleted`, which is already true for the entire
 * finalize phase; watchOrphanedScanState returns early while this tab still
 * owns the scanId; and runFinalizeScan's catch only sees rejections — an await
 * that never settles is not one. So a submission could freeze at 99% forever
 * with no error, which is exactly what 8 of 477 prod submissions did.
 *
 * Sized from production, not guessed. Finalize latency on SUCCESSFUL
 * submissions, 2026-08-01..25, `[SubmitScan] mounted` -> `finalizeScan() —
 * success`:
 *
 *     n 466 · p50 5.8s · p90 27.6s · p99 72.6s · max 228.5s
 *
 * Any threshold below ~230s would abort submissions that currently succeed.
 * 300s sits above the observed max, matches UPLOAD_STALL_TIMEOUT_MS, and stays
 * inside the backend's 10-minute SUBMITTING_TTL_SECONDS so the claim is
 * released before the sweep can reap the doc. It must also accommodate the
 * "recover" branch, which legitimately re-runs /bin-predict (~30-60s, longer on
 * retries) plus /upload-photos.
 *
 * Phase 1 pulled the photo upload out of the critical path, which should
 * shorten this tail considerably — but DO NOT lower this on that assumption
 * without re-measuring the percentile first.
 */
const FINALIZE_STALL_TIMEOUT_MS = 5 * 60 * 1000;

/**
 * How many times the finalize watchdog may re-queue before giving up and
 * surfacing an error. Re-queueing is strictly better UX than an error when the
 * scan is still live (the commit may simply be slow), but an unbounded re-queue
 * on a deterministic hang is an infinite loop of 5-minute timers.
 */
const MAX_FINALIZE_STALL_RETRIES = 1;

export class ScanUploader {
  private static instance: ScanUploader;
  private rotationMgr = RotationManager.getInstance();
  private uploadStarted = false;
  private uploadRetryCount = 0;
  private retryTimeout: NodeJS.Timeout | null = null;
  private frames: (Blob | null)[] = Array(22).fill(null);
  private allFrames: (Blob | null)[] = Array(44).fill(null);
  private keypoints: any[] = Array(44).fill(null);
  private normalizedKeypoints: any[] = Array(44).fill(null);
  private allStates: any[] = Array(44).fill(null);
  private partialStates: any[] = Array(22).fill(null);
  private gravityString: string = "";
  private scanId: string = "";
  private listeners = new Map<keyof UploadEventMap, Set<(p: any) => void>>();
  private uploadProgress = 0;
  private docUploadProgress = 0;
  private fetchUploadProgress = 0;
  private progressInterval: NodeJS.Timeout | null = null;
  private isDev = !isProdEnv;
  private scanSubmitted = false;
  private uploadCompleted = false;
  // uid captured once at upload() start (ensureAuthedUser().uid). The
  // upload_processing listener is keyed on THIS, not auth.currentUser, which can
  // be null under Failure mode G. It is the same uid /bin-predict uses. See
  // HERE_BE_DRAGONS.md § "Scan upload pipeline".
  private uploadUserId: string | null = null;
  private uploadPercentageListener: Unsubscribe | null = null;
  private segmentationAbortController: AbortController | null = null;
  private uploadPhotosAbortController: AbortController | null = null;
  private worker: Worker | null = null;
  private rotationSuccess = false;
  private scanVersion = 0; // incremented on each new scan to ignore stale worker messages
  private storePhotosEnabled = false;
  private segmentationVersion: string | null = null;
  private finalizing = false;
  private finalizeTicker: NodeJS.Timeout | null = null;
  private heartbeatInterval: NodeJS.Timeout | null = null;
  private stallTimeout: NodeJS.Timeout | null = null;
  private finalizeTimeout: NodeJS.Timeout | null = null;
  private finalizeStallRetries = 0;
  private finalizeGeneration = 0;
  private foregroundAbortPending = false;
  // Non-null only while a retry loop that WANTS the foreground abort is
  // running — i.e. /bin-predict. /upload-photos deliberately opts out (2a), so
  // without this the shared `foregroundAbortPending` flag would be set during a
  // photo upload that was never actually cancelled, and the next genuine
  // network error in that loop would be misread as our own cancellation.
  private foregroundAbortLabel: string | null = null;
  private retryTimeoutResolve: (() => void) | null = null;

  private constructor() {
    this.worker = new Worker(
      new URL("@/workers/frameWorker.ts", import.meta.url),
      { type: "module" },
    );
    this.worker.onmessage = (e) => {
      const { blob, ticksCount, isDev, error, scanVersion } = e.data;
      if (error) {
        logError("Frame worker error", {
          error,
          ticksCount,
          isDev,
          scanVersion,
        });
      }
      this.handleBlobFromWorker(blob, ticksCount, isDev, scanVersion);
    };
    this.rotationMgr.on(RotationEvents.PROGRESS, (e) => {
      this.processProgress(e);
    });
    this.rotationMgr.on(RotationEvents.SUCCESS, () => {
      logInfo("[ScanUploader] RotationEvents.SUCCESS received", {
        scanVersion: this.scanVersion,
        uploadStarted: this.uploadStarted,
        rotationSuccess: this.rotationSuccess,
        filledFrames: this.frames.filter((f) => f !== null).length,
      });
      this.rotationSuccess = true;
      this.uploadStarted = true; // keep this to preventing resetting the existing data before upload
      this.preUpload();
    });
    this.rotationMgr.on(RotationEvents.RESET, () => {
      logInfo("[ScanUploader] reset() called from RotationEvents.RESET", {
        scanId: this.scanId,
        uploadStarted: this.uploadStarted,
        scanVersion: this.scanVersion,
      });
      this.reset();
    });
    this.rotationMgr.on(RotationEvents.ERROR, (err) => {
      this.processRotationError(err?.message || "unknown error");
    });
  }

  /* -------------------- Event API -------------------- */
  on<K extends keyof UploadEventMap>(e: K, fn: (p: UploadEventMap[K]) => void) {
    if (!this.listeners.has(e)) this.listeners.set(e, new Set());
    this.listeners.get(e)?.add(fn as any);
    return () => this.listeners.get(e)?.delete(fn as any);
  }

  private emit<K extends keyof UploadEventMap>(e: K, p: UploadEventMap[K]) {
    this.listeners.get(e)?.forEach((cb) => cb(p as any));
  }

  /* ---------------------- PUBLIC METHODS ---------------------- */

  static getInstance() {
    if (!ScanUploader.instance) {
      ScanUploader.instance = new ScanUploader();
    }
    return ScanUploader.instance;
  }

  setGravityString(gravityString: string) {
    // only set the string if upload hasn't started
    if (!this.uploadStarted) {
      this.gravityString = gravityString;
      return;
    }
    // set it if upload started only if the string is empty
    if (!this.gravityString) {
      this.gravityString = gravityString;
    }
  }

  submitScan(storePhotosEnabled: boolean = false) {
    this.scanSubmitted = true;
    this.storePhotosEnabled = storePhotosEnabled;
    if (this.uploadCompleted) {
      this.finalizeScan();
    }
  }

  getScanId() {
    return this.scanId;
  }

  discardScan() {
    logInfo("[ScanUploader] reset() called from discardScan()", {
      scanId: this.scanId,
      uploadStarted: this.uploadStarted,
      scanVersion: this.scanVersion,
    });
    this.uploadStarted = false;
    this.uploadCompleted = false;
    this.rotationSuccess = false;
    this.emit(UploadEvents.DISCARDED, undefined);
    this.reset();
  }

  /* ---------------------- PRIVATE METHODS ---------------------- */

  private async processProgress(e: RotationEventData) {
    // console only — NOT logInfo. This runs once per rotation tick (44 per
    // scan), and measured on a real device these three per-frame lines were
    // 200 of the 338 pwa_logs rows a single scan journey wrote — 59% of the
    // writes, carrying less information than one `scan.rotation.summary` row.
    // See docs/telemetry/PLAN.md §11.1 (never log in a hot loop) and the
    // aggregate that replaces them in src/telemetry/rotationAccumulator.ts.
    console.debug("[ScanUploader] rotation event", {
      ticksCount: e.ticksCount,
      state: e.state,
      scanVersion: this.scanVersion,
    });
    // convert frame to base64 form ImageData
    // for every second tick
    this.toBlob(e.frame, e.ticksCount); // the tick count logic is in the toBlob method
    if (e.ticksCount % 2 === 0) {
      this.partialStates[(e.ticksCount - 2) / 2] = e.state;
    }
    if (this.isDev) {
      this.keypoints[e.ticksCount - 1] = e.keypoints;
      this.normalizedKeypoints[e.ticksCount - 1] = e.lastNorm;
      this.allStates[e.ticksCount - 1] = e.state;
    }
  }

  private async processRotationError(errorMsg: string) {
    if (this.isDev) {
      // Capture all required data upfront to ensure resilience
      const capturedData = {
        isDev: this.isDev,
        allFrames: [...this.allFrames],
        keypoints: JSON.parse(JSON.stringify(this.keypoints)),
        normalizedKeypoints: JSON.parse(
          JSON.stringify(this.normalizedKeypoints),
        ),
        allStates: JSON.parse(JSON.stringify(this.allStates)),
      };

      // Define upload functions that use captured data instead of class properties
      const uploadImages = async (scanId: string) => {
        try {
          const user = auth.currentUser;
          if (!user) return "user not found";
          const basePath = `scans_debug/${user?.uid}/${scanId}/body/images/`;
          const uploadTasks = capturedData.allFrames.map(
            async (frame, index) => {
              if (!frame) return;
              const fileName = `snapshot${index}.jpg`;
              const storageRef = ref(storage, `${basePath}${fileName}`);
              await uploadBytes(storageRef, frame);
            },
          );
          await Promise.all(uploadTasks);
        } catch (error) {
          logError("Error uploading images:", error as Error);
        }
      };

      const uploadKeypoints = async (scanId: string) => {
        try {
          const user = auth.currentUser;
          if (!user) return "user not found";
          const basePath = `scans_debug/${user?.uid}/${scanId}/body/keypoints.json`;
          const storageRef = ref(storage, basePath);
          const blob = new Blob([JSON.stringify(capturedData.keypoints)], {
            type: "application/json",
          });
          await uploadBytes(storageRef, blob);
        } catch (error) {
          logError("Error uploading keypoints:", error as Error);
        }
      };

      const uploadNormalizedJson = async (scanId: string) => {
        try {
          const user = auth.currentUser;
          if (!user) return "user not found";
          const basePath = `scans_debug/${user?.uid}/${scanId}/body/normalized.json`;
          const storageRef = ref(storage, basePath);
          const blob = new Blob(
            [JSON.stringify(capturedData.normalizedKeypoints)],
            {
              type: "application/json",
            },
          );
          await uploadBytes(storageRef, blob);
        } catch (error) {
          logError("Error uploading normalized keypoints:", error as Error);
        }
      };

      const uploadRotationJson = async (scanId: string) => {
        try {
          const user = auth.currentUser;
          if (!user) return "user not found";
          const basePath = `scans_debug/${user?.uid}/${scanId}/body/rotation.json`;
          const storageRef = ref(storage, basePath);
          const blob = new Blob([JSON.stringify(capturedData.allStates)], {
            type: "application/json",
          });
          await uploadBytes(storageRef, blob);
        } catch (error) {
          logError("Error uploading rotation json:", error as Error);
        }
      };

      this.emit(UploadEvents.DEBUG_UPLOAD, {
        debugScanId: "",
        isDebugUpload: true, // this is just to signal that debug upload is starting
      });
      // Execute the process in sequence
      const scanId = await ScanModel.createDebugDocument(errorMsg);
      if (!scanId) {
        this.emit(UploadEvents.DEBUG_UPLOAD, {
          debugScanId: "",
          isDebugUpload: false, // here we signal that debug upload failed (no need to loading)
        });
        return "failed to create debug document";
      }
      this.emit(UploadEvents.DEBUG_UPLOAD, {
        debugScanId: scanId,
        isDebugUpload: true, // here it still did not finish
      });
      await uploadImages(scanId);
      await uploadKeypoints(scanId);
      await uploadNormalizedJson(scanId);
      await uploadRotationJson(scanId);
      this.emit(UploadEvents.DEBUG_UPLOAD, {
        debugScanId: scanId,
        isDebugUpload: false, // here it finally finished
      });
      return "success";
    }
    return "dev only";
  }

  private preUpload() {
    const allFramesFilled = this.frames.every((frame) => frame !== null);
    const filledCount = this.frames.filter((f) => f !== null).length;
    // console only — per-tick. Aggregated into `scan.rotation.summary`.
    console.debug("[ScanUploader] preUpload() check", {
      rotationSuccess: this.rotationSuccess,
      allFramesFilled,
      filledFrames: filledCount,
      totalFrames: this.frames.length,
      uploadStarted: this.uploadStarted,
      scanVersion: this.scanVersion,
    });
    if (this.rotationSuccess) {
      // make sure the frames array has the 22 images
      if (allFramesFilled) {
        void this.upload();
      }
    }
  }

  private isRetryableError(error: any, status?: number): boolean {
    // AbortError means intentional cancellation (discard/reset) — NOT retryable
    if (error?.name === "AbortError") return false;
    // Network errors (fetch throws TypeError for network failures)
    if (error instanceof TypeError) return true;
    // Server errors (5xx) are retryable
    if (status && status >= 500) return true;
    // 409 is handled separately (new scanId), not a retry
    // 4xx errors (auth, validation) are NOT retryable
    return false;
  }

  private async callEndpointWithRetry(user?: User): Promise<void> {
    await this.callWithRetry("segmentation", () => this.callEndpoint(user), {
      handle409: true,
      // /bin-predict keeps the foreground abort: it is a different endpoint
      // with different semantics (409 -> regenerate scanId), and it is out of
      // scope for BUG-0007 2a. Assess separately.
      abortOnForeground: true,
    });
  }

  private async callUploadPhotosWithRetry(): Promise<void> {
    // No abortOnForeground: see UPLOAD_PHOTOS_TIMEOUT_MS. This request is
    // bounded by its own timeout instead, because the foreground abort was
    // cancelling requests the server had already completed.
    await this.callWithRetry("upload-photos", () =>
      this.callUploadPhotosEndpoint(),
    );
  }

  /**
   * Generic retry loop used by both the segmentation upload and the opt-in
   * photo upload. Both endpoints share the same retry semantics (3 retries,
   * 5/10/20s exponential backoff, retryable on 5xx / TypeError network errors,
   * non-retryable on 4xx / AbortError).
   *
   * The `handle409` flag is used only by the segmentation call, which treats
   * 409 (scanId already exists server-side) as a signal to regenerate the
   * scanId and start over with a fresh retry budget.
   */
  private async callWithRetry(
    label: string,
    requestFn: () => Promise<{
      ok: boolean;
      status: number;
      statusText: string;
      data: any;
    }>,
    options: { handle409?: boolean; abortOnForeground?: boolean } = {},
  ): Promise<void> {
    this.uploadRetryCount = 0;
    const startingScanVersion = this.scanVersion;

    // Advertise to onForegrounded whether THIS loop wants its request
    // cancelled on resume. Only /bin-predict does.
    this.foregroundAbortLabel = options.abortOnForeground ? label : null;
    document.addEventListener("visibilitychange", this.handleForeground);
    try {
      while (this.uploadRetryCount <= MAX_UPLOAD_RETRIES) {
        if (this.scanVersion !== startingScanVersion) {
          logInfo(
            `[ScanUploader] ${label} retry loop aborted — scan was reset/discarded`,
          );
          return;
        }
        try {
          const res = await requestFn();

          if (options.handle409 && res.status === 409) {
            logInfo(
              `[ScanUploader] ${label} 409 conflict, regenerating scanId and retrying`,
            );
            this.setScanId();
            this.uploadRetryCount = 0;
            continue;
          }

          if (res.status === 200) {
            logInfo(`[ScanUploader] ${label} succeeded`, {
              scanId: this.scanId,
              scanVersion: this.scanVersion,
            });
            return;
          }

          const error = new Error(
            `${label} failed with status ${res.status}: ${res.statusText}`,
          );
          if (!this.isRetryableError(error, res.status)) {
            throw error;
          }

          this.uploadRetryCount++;
          if (this.uploadRetryCount > MAX_UPLOAD_RETRIES) {
            throw error;
          }
          const delay =
            RETRY_BASE_DELAY_MS * Math.pow(2, this.uploadRetryCount - 1);
          logInfo(`[ScanUploader] Retrying ${label} after server error`, {
            attempt: this.uploadRetryCount,
            maxRetries: MAX_UPLOAD_RETRIES,
            delayMs: delay,
            status: res.status,
          });
          await this.delay(delay);
        } catch (error: any) {
          // Foreground-triggered aborts: the OS suspended the fetch while the
          // app was backgrounded. Don't count against the retry budget — the
          // user actively returned to the app and we should try again cleanly.
          //
          // BUG-0007 2b. This branches on OUR OWN flag, never on the error
          // name. WebKit does not reliably reject an aborted fetch with
          // `AbortError`: when the abort lands while the request body is still
          // uploading — the normal state for a multi-MB multipart POST on a
          // mobile uplink — it surfaces as `TypeError: Load failed`. The old
          // `error?.name === "AbortError" && ...` test therefore missed, the
          // cancel fell through to the network-error branch, a retry was
          // charged, a 5s backoff started instead of a clean retry, AND the
          // flag was left true for the next error to misread. See
          // ~/.claude/TRAPS.md, "Safari / WebKit — AbortController.abort() ...
          // can surface as TypeError: Load failed". Pinned by tests T4 and T5.
          if (this.foregroundAbortPending) {
            this.foregroundAbortPending = false;
            logInfo(`[ScanUploader] ${label} foreground-abort — retrying`, {
              scanId: this.scanId,
              scanVersion: this.scanVersion,
              graceMs: FOREGROUND_RETRY_GRACE_MS,
              errorName: error?.name,
              errorMessage: error?.message,
            });
            // 2c: do not retry into a radio that has only just woken.
            await this.delay(FOREGROUND_RETRY_GRACE_MS);
            continue;
          }
          logError(`[ScanUploader] ${label} threw error`, {
            scanId: this.scanId,
            scanVersion: this.scanVersion,
            attempt: this.uploadRetryCount,
            errorMessage: error?.message,
            errorName: error?.name,
          });
          if (this.isRetryableError(error)) {
            this.uploadRetryCount++;
            if (this.uploadRetryCount > MAX_UPLOAD_RETRIES) {
              throw error;
            }
            const delay =
              RETRY_BASE_DELAY_MS * Math.pow(2, this.uploadRetryCount - 1);
            logInfo(`[ScanUploader] Retrying ${label} after network error`, {
              attempt: this.uploadRetryCount,
              maxRetries: MAX_UPLOAD_RETRIES,
              delayMs: delay,
              errorMessage: error?.message,
            });
            await this.delay(delay);
            continue;
          }
          throw error;
        }
      }
    } finally {
      document.removeEventListener("visibilitychange", this.handleForeground);
      this.foregroundAbortLabel = null;
      // Never let a set-but-unconsumed flag survive this loop. A request that
      // was aborted and then succeeded anyway leaves it true, and the next
      // loop's first genuine network error would read as an intentional
      // cancel. BUG-0007 2b, pinned by test T5.
      this.foregroundAbortPending = false;
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => {
      this.retryTimeoutResolve = resolve;
      this.retryTimeout = setTimeout(resolve, ms);
    });
  }

  // Arrow function so addEventListener/removeEventListener receive the same
  // reference and the listener can be cleaned up without a wrapper. The body
  // lives on the prototype so it is reachable from unit tests, which drive
  // this class through hand-built `this` objects and cannot see instance
  // properties (the constructor spawns a Worker and never runs under jsdom).
  private handleForeground = () => this.onForegrounded();

  private onForegrounded() {
    if (document.visibilityState !== "visible") return;
    // Re-assert liveness the moment we're foregrounded: background timer
    // throttling can have let the heartbeat go stale while this page was
    // suspended but very much alive.
    if (this.scanId) this.writeHeartbeat();
    if (!this.uploadStarted) return;
    // A suspended page can come back with the stall deadline already past
    // while an `uploaded: true` snapshot is still queued. Guarantee some
    // runway so the listener gets its chance before we throw the scan away.
    if (!this.uploadCompleted) {
      this.startStallWatchdog(STALL_FOREGROUND_GRACE_MS);
    }
    // BUG-0007 2a. Only the loop that asked for it gets its request cancelled,
    // and /upload-photos deliberately does not ask: prod logged 253/253
    // successes on that endpoint at p50 4.0s, and one measured abort landed
    // 6.7s AFTER the server had written `saved 22 originals`, then queued a
    // ~6MB re-upload of photos GCS already held (5 of 246 opt-in scans uploaded
    // their photos more than once; one uploaded four times). It is bounded by
    // UPLOAD_PHOTOS_TIMEOUT_MS instead.
    //
    // The label check also keeps `foregroundAbortPending` honest: setting it
    // during a photo upload we never cancelled would make that loop's next
    // genuine network error read as an intentional cancel.
    if (this.foregroundAbortLabel !== "segmentation") return;
    this.foregroundAbortPending = true;
    this.segmentationAbortController?.abort();
    // Resolve any pending retry delay immediately so the loop doesn't wait.
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    this.retryTimeoutResolve?.();
    this.retryTimeoutResolve = null;
    logInfo("[ScanUploader] Foregrounded during upload — aborting current attempt to retry", {
      scanId: this.scanId,
      scanVersion: this.scanVersion,
    });
  }

  private async upload() {
    this.setScanId();
    this.uploadStarted = true;
    this.startStallWatchdog();
    // Capture ONE authed user for the whole upload. We thread this exact user
    // into /bin-predict (so every retry uses one uid) AND key the
    // upload_processing listener on its uid (this.uploadUserId) below. Under
    // Failure mode G auth.currentUser can be null here even after a valid
    // sign-in, and a second independent ensureAuthedUser() could mint a
    // DIFFERENT anon uid — which would make watchUploadPercentage query the
    // wrong doc and never observe uploaded:true (Submit silently stuck).
    let user: User;
    try {
      user = await ensureAuthedUser();
    } catch (error: any) {
      // Mirror the endpoint-failure path below: surface ERROR and tear down.
      // uploadStarted must flip false BEFORE reset() or reset() no-ops.
      logError("[ScanUploader] upload() auth failed before /bin-predict", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
        errorMessage: error?.message,
      });
      this.uploadStarted = false;
      this.emit(UploadEvents.ERROR, error);
      this.reset();
      return;
    }
    this.uploadUserId = user.uid;
    // Bail if the scan was discarded/reset while we were signing in. Under
    // Mode G the ensureAuthedUser() above is a real network round-trip, during
    // which discardScan() can flip uploadStarted=false and reset() can wipe
    // scanId/uploadUserId. Without this guard upload() would resume against
    // cleared state — firing /bin-predict with an empty scanId (spurious ERROR
    // on top of the already-emitted DISCARDED) and attaching a stale listener.
    // (A bare RESET event can't reach here: reset() no-ops while uploadStarted
    // is true, so only an explicit discard clears it during the await.)
    if (!this.uploadStarted) {
      logInfo("[ScanUploader] upload() aborted — scan discarded during sign-in", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
      });
      return;
    }
    logInfo("[ScanUploader] upload() started", {
      scanId: this.scanId,
      scanVersion: this.scanVersion,
      framesCount: this.frames.filter((f) => f !== null).length,
      hasGravity: !!this.gravityString,
      rotationSuccess: this.rotationSuccess,
    });
    this.callEndpointWithRetry(user).catch((error) => {
      // AbortError means the in-flight /bin-predict was cancelled by an
      // intentional state transition that already owns its own UX:
      //   - finalizeScan() success → reset() → abort (SUCCESS already emitted)
      //   - discardScan() → reset() → abort (DISCARDED already emitted)
      //   - rotation reset/error → reset() → abort (rotation UI shown)
      // We can't gate on uploadCompleted here because reset() flips it to
      // false synchronously *before* the aborted fetch's promise rejection
      // lands in this microtask. isRetryableError already classifies
      // AbortError as non-retryable, so by this point the retry loop has
      // exited cleanly — there is no real failure to surface.
      if (error?.name === "AbortError") {
        logInfo("[ScanUploader] callEndpointWithRetry aborted — ignoring", {
          scanId: this.scanId,
          scanVersion: this.scanVersion,
          errorMessage: error?.message,
        });
        return;
      }
      logError("[ScanUploader] Upload failed after all retries", {
        retryCount: this.uploadRetryCount,
        errorMessage: error?.message,
      });
      this.uploadStarted = false;
      logInfo(
        "[ScanUploader] reset() called from upload() catch — endpoint failed",
        {
          scanId: this.scanId,
          scanVersion: this.scanVersion,
          retryCount: this.uploadRetryCount,
          errorMessage: error?.message,
        },
      );
      this.emit(UploadEvents.ERROR, error);
      this.reset();
    });
    this.watchUploadPercentage();
    const session = localStorage.getItem("sessionId");
    if (session) {
      updateDoc(doc(db, "widget_sessions", session), {
        progress: "SUBMITTING",
        activeScan: null,
      });
    }
  }

  private onUploadCompleted() {
    this.uploadCompleted = true;
    this.clearStallWatchdog();
    this.emit(UploadEvents.COMPLETED, undefined);
    if (this.scanSubmitted) {
      this.finalizeScan();
    }
  }

  /**
   * Run the post-upload finalization sequence. Called once per scan, the first
   * time both (segmentation upload completed) AND (user pressed Submit) are true.
   *
   * Sequence (order changed by BUG-0007 Phase 1 — the photo upload no longer
   * gates the commit):
   *   1. If the user opted in, START /upload-photos with retry. Do NOT await
   *      it: it is opt-in retention/sharing and the backend explicitly does
   *      not touch scans/{scanId}.
   *   2. Create scans/{scanId} via ScanModel.create — the only write that
   *      triggers downstream reconstruction/avatar pipelines.
   *   3. Emit SUCCESS, then await the photo upload before reset() so teardown
   *      cannot pull the frames out from under an in-flight attempt.
   *   4. ERROR if the auth, claim or commit step fails. A photo-upload failure
   *      is logged and absorbed — it never fails the scan.
   *
   * Bounded by the finalize watchdog (FINALIZE_STALL_TIMEOUT_MS, Phase 3): a
   * step that neither resolves nor rejects is not something the catch below
   * can see.
   */
  private finalizeScan() {
    if (this.finalizing) {
      logInfo("[ScanUploader] finalizeScan() skipped — already finalizing", {
        scanId: this.scanId,
      });
      return;
    }
    this.finalizing = true;
    // Fence the watchdog to THIS attempt. A re-queued finalize arms a new
    // timer while the previous (hung) runFinalizeScan promise is still
    // pending; without the generation check, that stale promise settling later
    // would disarm the live attempt's watchdog. Same fencing the audio
    // watchdog uses — see CHANGELOG [2.5.12].
    const generation = ++this.finalizeGeneration;
    this.startFinalizeWatchdog();
    void this.runFinalizeScan().finally(() => {
      if (this.finalizeGeneration === generation) this.clearFinalizeWatchdog();
    });
  }

  /**
   * Sweep-race recovery: orphan-cleanup may have deleted the GCS prefix
   * (silhouettes / gravity / rotation) and flagged upload_processing.resolved
   * while the user was on NewAvatarScreen — or it may fire DURING finalize
   * itself if uploads stretch past the staleness threshold. We close both
   * windows with a single Firestore transaction that atomically:
   *   1. Reads scans/{scanId} and upload_processing/{scanId}.
   *   2. Routes to one of four branches based on observed state.
   *   3. On "normal" or "recover" with an existing doc, writes
   *      `submitting_timestamp: now` in the same transaction so any
   *      concurrent sweep/discard-trigger sees the claim atomically.
   *
   * Why a transaction rather than write-then-read: the sweep is a separate
   * writer on the same doc. With a read-modify-write sequence, the sweep
   * could flip discarded_stale=true between our claim write and our state
   * read, and we'd miss the recover signal. The transaction's optimistic
   * concurrency forces a retry when that happens, so we always see (and
   * write atomically against) the latest committed state.
   *
   * Backend defense in depth: even if a transaction window is somehow
   * missed, onDiscardUpdate also re-checks submitting_timestamp before
   * deleting GCS (see SUBMITTING_TTL_SECONDS in firebase_utils.py).
   *
   * Branches:
   *  - "recover":   resolved=true | discarded_stale=true | doc missing
   *                 entirely (with scans missing and not user-discarded).
   *                 Re-run /bin-predict (and /upload-photos when opted in)
   *                 before ScanModel.create. When the doc exists we claim
   *                 it in this tx so segmentation's set(merge=True) re-run
   *                 (which preserves submitting_timestamp) keeps the claim
   *                 alive through recovery. When the doc is missing we
   *                 don't claim — segmentation creates a fresh doc with
   *                 created_timestamp=now (sweep can't reap it on age),
   *                 and refreshSubmittingClaim() runs post-segmentation.
   *  - "duplicate": scans/{id} already exists. No-op success; essentially
   *                 unreachable on a single device (finalizing guard +
   *                 fresh UUID scanIds) but defensive against cross-device
   *                 replay.
   *  - "discarded": discarded=true. External discard from another tab/admin;
   *                 honor user intent. No claim written so the queued
   *                 onDiscardUpdate can run GCS cleanup.
   *  - "normal":    happy path. upload_processing exists and is unresolved
   *                 and not flagged by any cleanup path.
   */
  private async claimAndCheckRecovery(): Promise<
    "normal" | "recover" | "duplicate" | "discarded"
  > {
    const upRef = doc(db, "upload_processing", this.scanId);
    const scanRef = doc(db, "scans", this.scanId);
    try {
      return await runTransaction(db, async (tx) => {
        const scanSnap = await tx.get(scanRef);
        if (scanSnap.exists()) return "duplicate";

        const upSnap = await tx.get(upRef);
        if (!upSnap.exists()) {
          // No doc to claim — segmentation re-run will create it fresh,
          // and refreshSubmittingClaim() right after will stamp it.
          return "recover";
        }
        const data = upSnap.data() ?? {};

        if (data.discarded === true) {
          // User-initiated discard — don't claim, let onDiscardUpdate run.
          return "discarded";
        }

        const claim = { submitting_timestamp: Math.floor(Date.now() / 1000) };
        if (data.resolved === true || data.discarded_stale === true) {
          // Sweep already flagged OR onDiscardUpdate already ran. Hold a
          // claim so the recovery re-segmentation (which reopens the doc
          // via merge=True with resolved/discarded_stale cleared) is not
          // immediately re-flagged by the next sweep tick.
          tx.update(upRef, claim);
          return "recover";
        }

        // Clean state — claim it.
        tx.update(upRef, claim);
        return "normal";
      });
    } catch (e) {
      logError("[ScanUploader] claimAndCheckRecovery transaction failed", {
        scanId: this.scanId,
        errorMessage: (e as Error)?.message,
      });
      // Conservative fallback: proceed with the normal path so we don't
      // strand a finalize on a transient Firestore error. Without a claim
      // we're back to the original race window, but onDiscardUpdate's
      // TTL check is unaffected by this branch and still provides defense
      // in depth if a sweep happens to race the next few hundred ms.
      return "normal";
    }
  }

  private async runFinalizeScan() {
    let recoveryStatus: "normal" | "recover" | "duplicate" | "discarded" =
      "normal";
    // BUG-0007 instrumentation. Finalize had NO entry log, so a submission
    // that froze here was indistinguishable from one where finalizeScan() was
    // never called at all — 5 of 8 lost prod scans could not be placed. These
    // four lines bracket every await that can hang (auth → claim → commit), so
    // the LAST line present names the stage that hung. Once per submission, not
    // a hot loop: exempt from the per-frame rule in CLAUDE.md /
    // docs/telemetry/PLAN.md §11.1. Do NOT demote these to console.debug.
    // `storePhotosEnabled` rides on all four on purpose — it was previously
    // written only on the success line, so a FAILED finalize never recorded
    // whether the opt-in photo upload was even in its path, which is precisely
    // the discrimination this bug needed and could not make.
    logInfo("[ScanUploader] finalizeScan() entered", {
      scanId: this.scanId,
      scanVersion: this.scanVersion,
      storePhotosEnabled: this.storePhotosEnabled,
      uploadCompleted: this.uploadCompleted,
      scanSubmitted: this.scanSubmitted,
    });
    try {
      // Scan-time re-auth guard (Failure mode G) for the finalize boundary.
      // Auth can be evicted again in the seconds/minutes between upload and the
      // user tapping Submit, and every finalize write — the claimAndCheckRecovery
      // runTransaction claim AND the ScanModel.create batch — is enforced by
      // Firestore security rules. Re-auth ONCE so those writes authenticate,
      // and thread the credential's uid into ScanModel.create. Do NOT re-read
      // auth.currentUser for the uid: it can stay null on the affected iOS
      // contexts even after a successful anon sign-in. See HERE_BE_DRAGONS.md
      // § "Auth flow". If the single sign-in rejects, it propagates to the
      // catch below (the existing ERROR/reset UX path) — no retry, no loop.
      const user = await ensureAuthedUser();
      logInfo("[ScanUploader] finalizeScan() — auth resolved", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
        storePhotosEnabled: this.storePhotosEnabled,
        uidPrefix: user.uid.slice(0, 8),
      });

      recoveryStatus = await this.claimAndCheckRecovery();
      logInfo("[ScanUploader] finalizeScan() — claim resolved", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
        storePhotosEnabled: this.storePhotosEnabled,
        recoveryStatus,
      });

      if (recoveryStatus === "duplicate") {
        logInfo(
          "[ScanUploader] finalizeScan() — scan already submitted, no-op",
          { scanId: this.scanId, scanVersion: this.scanVersion },
        );
        this.uploadProgress = 100;
        this.emit(UploadEvents.PROGRESS, this.uploadProgress);
        this.uploadStarted = false;
        this.emit(UploadEvents.SUCCESS, undefined);
        this.reset();
        return;
      }

      if (recoveryStatus === "discarded") {
        logError(
          "[ScanUploader] finalizeScan() — scan was discarded externally",
          { scanId: this.scanId, scanVersion: this.scanVersion },
        );
        this.uploadStarted = false;
        // Release the just-written claim so the queued onDiscardUpdate
        // (which currently sees submitting_timestamp and bails) can run
        // GCS cleanup as the user intended.
        this.releaseSubmittingClaim();
        this.emit(
          UploadEvents.ERROR,
          new Error("This scan was discarded. Please rescan."),
        );
        this.reset();
        return;
      }

      // Animate the bar across the finalize phase (90→99%). SubmitScan
      // often mounts after segmentation already emitted its final PROGRESS
      // event, so without re-emitting here the bar would sit at 0% until
      // SUCCESS. Recovery adds a re-segmentation round trip (~5–15s) plus
      // potentially /upload-photos (~2–4s); extend the ticker so the bar
      // doesn't pin at 99% for several seconds in that case.
      const FINALIZE_START = 90;
      const FINALIZE_TARGET = 99;
      const baseDurationMs = this.storePhotosEnabled ? 6000 : 1500;
      const finalizeDurationMs =
        recoveryStatus === "recover" ? baseDurationMs + 15000 : baseDurationMs;
      const startProgress = Math.max(this.uploadProgress, FINALIZE_START);
      this.uploadProgress = startProgress;
      this.emit(UploadEvents.PROGRESS, this.uploadProgress);
      const tickStart = Date.now();
      this.finalizeTicker = setInterval(() => {
        const t = Math.min((Date.now() - tickStart) / finalizeDurationMs, 1);
        const next = startProgress + (FINALIZE_TARGET - startProgress) * t;
        if (next > this.uploadProgress) {
          this.uploadProgress = next;
          this.emit(UploadEvents.PROGRESS, this.uploadProgress);
        }
        if (t >= 1 && this.finalizeTicker) {
          clearInterval(this.finalizeTicker);
          this.finalizeTicker = null;
        }
      }, 100);

      if (recoveryStatus === "recover") {
        logInfo(
          "[ScanUploader] finalizeScan() recovering from sweep race — re-running /bin-predict",
          {
            scanId: this.scanId,
            scanVersion: this.scanVersion,
            storePhotosEnabled: this.storePhotosEnabled,
          },
        );
        // Re-run segmentation. The watchUploadPercentage listener detached
        // itself after the first uploaded=true, so the per-image percent
        // updates from this re-run won't re-fire onUploadCompleted. The
        // merge inside create_or_update_processing_document clears all
        // four flags (resolved / discarded / discarded_stale / processed)
        // atomically, so onDiscardUpdate sees a clean state and no-ops.
        await this.callEndpointWithRetry(user);
        // Refresh the claim: recovery just spent ~30–60s on segmentation
        // (longer on retries), and /upload-photos on a slow mobile link
        // can add another minute or two. Re-stamping resets the TTL clock
        // so the remaining finalize work has the full window of headroom.
        await this.refreshSubmittingClaim();
      }

      // BUG-0007. /upload-photos is OPT-IN retention/sharing, and the backend
      // is explicit that it "does NOT create or update scans/{scanId} — that
      // document's existence triggers downstream pipelines and is owned
      // exclusively by the frontend" (segmentation/main.py). So it must never
      // gate — or even delay — the one write that starts generation. Awaiting
      // it here destroyed 8 of 477 prod submissions over 24 days (1.68%), every
      // one of them with all 22 images already in GCS and only scans/{scanId}
      // missing; 6 of those 8 users never completed a scan afterwards.
      //
      // Start it, don't await it, and absorb its failure: a photo upload that
      // fails or hangs is a lost opt-in extra, never a lost scan. The .catch()
      // is what makes the later `await photoUpload` safe to leave unguarded.
      const photoUpload = this.storePhotosEnabled
        ? this.callUploadPhotosWithRetry().catch((err: any) => {
            logError(
              "[ScanUploader] upload-photos failed — scan is unaffected",
              {
                scanId: this.scanId,
                scanVersion: this.scanVersion,
                errorMessage: err?.message,
                errorName: err?.name,
              },
            );
          })
        : Promise.resolve();

      await ScanModel.create(this.scanId, {
        storePhotosEnabled: this.storePhotosEnabled,
        segmentationVersion: this.segmentationVersion,
        uid: user.uid,
      });
      logInfo("[ScanUploader] finalizeScan() — scans doc committed", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
        storePhotosEnabled: this.storePhotosEnabled,
        recoveryStatus,
      });
      // Disarm here, not only in finalizeScan()'s .finally(). The scan is
      // committed and safe, but this method does not RETURN until the
      // fire-and-forget photo upload settles (Phase 1 R2) — so on a hung photo
      // upload the .finally() never runs, and without this the watchdog would
      // fire and stack a spurious ERROR on top of an already-emitted SUCCESS.
      this.clearFinalizeWatchdog();

      if (this.finalizeTicker) {
        clearInterval(this.finalizeTicker);
        this.finalizeTicker = null;
      }
      this.uploadProgress = 100;
      this.emit(UploadEvents.PROGRESS, this.uploadProgress);
      this.uploadStarted = false;
      logInfo("[ScanUploader] reset() called from finalizeScan() — success", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
        storePhotosEnabled: this.storePhotosEnabled,
        recoveryStatus,
      });
      // Leave a positive trace of the success BEFORE reset() removes
      // scanID/timestamp. Those keys are what NewAvatarScreen's Step 1 reads,
      // so without this the next arrival there renders "never started" and the
      // user believes the scan was lost. Deliberately here and not inside
      // reset(): reset() also runs on RotationEvents.RESET, discardScan() and
      // the upload() error catch, where a marker would claim a success that
      // never happened.
      markScanSubmitted(this.scanId);
      this.emit(UploadEvents.SUCCESS, undefined);

      // MUST precede reset(). reset() sets scanId = "", frames =
      // Array(22).fill(null) and storePhotosEnabled = false, and
      // callUploadPhotosEndpoint reads all three AFTER its own awaits
      // (ensureAuthedUser + getIdToken) — so tearing down first makes an
      // in-flight retry throw "Scan ID is not set!" / "Not enough frames!".
      // Never throws: the .catch() above already absorbed it. BUG-0007 R2,
      // pinned by test T3.
      await photoUpload;
      this.reset();
    } catch (error: any) {
      if (this.finalizeTicker) {
        clearInterval(this.finalizeTicker);
        this.finalizeTicker = null;
      }
      this.uploadStarted = false;
      // AbortError here means discardScan() / reset() intentionally
      // cancelled the in-flight request — DISCARDED was already emitted by
      // the caller, so don't double-emit ERROR. Mirrors the swallow in
      // upload()'s catch for /bin-predict.
      // Best-effort release of the submitting_timestamp claim on every
      // non-success exit. Without it the doc would sit claimed for up to
      // SUBMITTING_TTL_SECONDS, blocking onDiscardUpdate's cleanup (in the
      // AbortError / user-discard case) or the next sweep cycle (genuine
      // failure case).
      this.releaseSubmittingClaim();
      if (error?.name === "AbortError") {
        // If the scan is still alive (not discarded/reset), the abort came
        // from the foreground handler or a controller collision — not from
        // an intentional discard. Clear the guard and re-queue so the next
        // onUploadCompleted or submitScan can re-enter finalizeScan cleanly.
        if (this.uploadStarted && this.uploadCompleted && this.scanSubmitted) {
          logInfo("[ScanUploader] finalizeScan aborted but scan is live — re-queuing", {
            scanId: this.scanId,
            scanVersion: this.scanVersion,
          });
          this.finalizing = false;
          this.finalizeScan();
          return;
        }
        logInfo("[ScanUploader] finalizeScan aborted — ignoring", {
          scanId: this.scanId,
          scanVersion: this.scanVersion,
          errorMessage: error?.message,
        });
        return;
      }
      logError("[ScanUploader] finalizeScan() failed", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
        storePhotosEnabled: this.storePhotosEnabled,
        recoveryStatus,
        errorMessage: error?.message,
      });
      this.emit(UploadEvents.ERROR, error);
      this.reset();
    }
  }

  private releaseSubmittingClaim() {
    // Fire-and-forget — failures here aren't actionable, and the
    // submitting_timestamp TTL bounds the worst-case impact.
    const scanId = this.scanId;
    if (!scanId) return;
    updateDoc(doc(db, "upload_processing", scanId), {
      submitting_timestamp: null,
    }).catch(() => {});
  }

  private async refreshSubmittingClaim(): Promise<void> {
    try {
      await updateDoc(doc(db, "upload_processing", this.scanId), {
        submitting_timestamp: Math.floor(Date.now() / 1000),
      });
    } catch (e) {
      // Non-fatal: the original claim is still in place. Worst case the
      // claim ages out mid-finalize and the next sweep flags the doc —
      // onDiscardUpdate's TTL check still applies as a backstop.
      logInfo("[ScanUploader] refreshSubmittingClaim failed — continuing", {
        scanId: this.scanId,
        errorMessage: (e as Error)?.message,
      });
    }
  }

  private reset() {
    logInfo("[ScanUploader] reset() entered", {
      scanVersion: this.scanVersion,
      uploadStarted: this.uploadStarted,
      scanId: this.scanId,
      rotationSuccess: this.rotationSuccess,
      uploadCompleted: this.uploadCompleted,
      scanSubmitted: this.scanSubmitted,
    });
    // don't reset if upload is in progress
    if (this.uploadStarted) {
      logInfo("[ScanUploader] reset() skipped — uploadStarted is true");
      return;
    }
    this.scanVersion++; // increment to ignore any stale worker messages from previous scan
    this.segmentationAbortController?.abort();
    this.uploadPhotosAbortController?.abort();
    if (this.retryTimeout) {
      clearTimeout(this.retryTimeout);
      this.retryTimeout = null;
    }
    this.uploadRetryCount = 0;
    this.allFrames = Array(44).fill(null);
    this.uploadStarted = false;
    this.uploadCompleted = false;
    this.rotationSuccess = false;
    this.frames = Array(22).fill(null);
    this.partialStates = Array(22).fill(null);
    this.scanId = "";
    this.uploadUserId = null;
    this.gravityString = "";
    this.uploadProgress = 0;
    this.docUploadProgress = 0;
    this.fetchUploadProgress = 0;
    this.progressInterval = null;
    this.keypoints = Array(44).fill(null);
    this.normalizedKeypoints = Array(44).fill(null);
    this.allStates = Array(44).fill(null);
    this.scanSubmitted = false;
    this.storePhotosEnabled = false;
    this.segmentationVersion = null;
    this.finalizing = false;
    this.finalizeStallRetries = 0;
    this.clearFinalizeWatchdog();
    if (this.finalizeTicker) {
      clearInterval(this.finalizeTicker);
      this.finalizeTicker = null;
    }
    this.uploadPercentageListener?.();
    this.uploadPercentageListener = null;
    if (this.progressInterval) clearInterval(this.progressInterval);
    this.progressInterval = null;
    this.emit(UploadEvents.RESET, undefined);
    // reset local storage
    this.clearStallWatchdog();
    this.stopHeartbeat();
    localStorage.removeItem("scanID");
    localStorage.removeItem("timestamp");
  }

  private updateProgress() {
    // Cap the segmentation-driven bar at 90% so the remaining 90→100 range
    // is reserved for the finalize phase (photo upload + scans/{scanId}
    // creation), giving the user a visible fill animation after Submit.
    const SEGMENTATION_CAP = 90;
    if (this.docUploadProgress === 100) {
      this.uploadProgress = SEGMENTATION_CAP;
      this.emit(UploadEvents.PROGRESS, this.uploadProgress);
      if (this.progressInterval) clearInterval(this.progressInterval);
      if (this.uploadPercentageListener) this.uploadPercentageListener();
      return;
    }
    if (this.fetchUploadProgress > 0 && this.docUploadProgress === 0) {
      this.uploadProgress = Math.min(
        this.fetchUploadProgress,
        SEGMENTATION_CAP,
      );
      this.emit(UploadEvents.PROGRESS, this.uploadProgress);
      return;
    }
    if (this.docUploadProgress > 0) {
      // clear the interval if it exists
      if (this.progressInterval) clearInterval(this.progressInterval);
      // now calculate the progress based on the fetch progress and doc progress
      const percentageRatio = (100 - this.fetchUploadProgress) / 100;
      const docProgress = this.docUploadProgress * percentageRatio;
      this.uploadProgress = Math.min(
        this.fetchUploadProgress + docProgress,
        SEGMENTATION_CAP,
      );
      this.emit(UploadEvents.PROGRESS, this.uploadProgress);
    }
  }

  private watchUploadPercentage() {
    // Key the listener on the uid captured at upload() time, NOT auth.currentUser.
    // Under Failure mode G auth.currentUser is null when this runs (Firebase
    // evicted it / the upload's ensureAuthedUser hasn't surfaced it), so reading
    // it here dropped the listener and Submit silently stalled. uploadUserId is
    // the SAME uid /bin-predict sent, so where("userId",...) matches the doc the
    // server created. See HERE_BE_DRAGONS.md § "Scan upload pipeline".
    if (!this.uploadUserId) {
      logError("User does not exist");
      return;
    }
    const q = query(
      collection(db, "upload_processing"),
      where("scanId", "==", this.scanId),
      where("userId", "==", this.uploadUserId),
    );
    this.uploadPercentageListener = onSnapshot(q, (snapshot) => {
      if (!snapshot.empty) {
        snapshot.forEach((doc) => {
          // clear the interval if it exists
          if (this.progressInterval) {
            clearInterval(this.progressInterval);
          }
          const data = doc.data();
          if (!data) {
            logInfo(
              "[ScanUploader] discardScan() called from watchUploadPercentage — doc data is null",
              {
                scanId: this.scanId,
                scanVersion: this.scanVersion,
              },
            );
            return this.discardScan();
          }
          if (data.discarded) {
            logInfo(
              "[ScanUploader] discardScan() called from watchUploadPercentage — discarded flag is true",
              {
                scanId: this.scanId,
                scanVersion: this.scanVersion,
              },
            );
            return this.discardScan();
          }
          // upload_processing.version is the sole source of truth for the
          // live segmentation version recorded on scans/{scanId}. The server
          // writes it inside create_or_update_processing_document at the
          // start of /bin-predict, so it's present before this listener can
          // fire — and it's race-free with the data.uploaded gate below,
          // which also reads from the same doc.
          if (data.version) {
            this.segmentationVersion = data.version;
          }
          // if uploaded then set the progress to 100
          if (data.uploaded) {
            this.docUploadProgress = 100;
            this.updateProgress();
            this.onUploadCompleted(); // this will set the uploadCompleted to true and emit the COMPLETED event
            return this.uploadPercentageListener?.(); // remove the listener
          }
          if (data.percent) {
            this.docUploadProgress = Math.min(data.percent, 100);
          }
          return this.updateProgress();
        });
      } else {
        // if the document doesn't exist then set the progress to 0
        // it means the fetch request has not started yet
        // in this case we need to start the interval
        this.progressInterval = setInterval(() => {
          this.fetchUploadProgress = Math.min(this.fetchUploadProgress + 1, 99);
          this.updateProgress();
        }, 3000); // expecting the full upload/processing to take around 5 minutes
      }
    });
  }

  /* ---------------------- HELPER METHODS ---------------------- */

  private toBlob(frame: ImageData, ticksCount: number) {
    if (!this.isDev && ticksCount % 2 !== 0) return;

    this.worker?.postMessage({
      frame,
      ticksCount,
      isDev: this.isDev,
      scanVersion: this.scanVersion,
    });
  }

  private handleBlobFromWorker(
    blob: Blob | null,
    ticksCount: number,
    isDev: boolean,
    scanVersion: number,
  ) {
    // Ignore stale messages from previous scans
    if (scanVersion !== this.scanVersion) {
      logInfo(
        "[ScanUploader] Ignored stale worker message (version mismatch)",
        {
          messageScanVersion: scanVersion,
          currentScanVersion: this.scanVersion,
          ticksCount,
        },
      );
      return;
    }

    if (!blob) {
      logError("Worker returned null blob", {
        ticksCount,
        isDev,
        message:
          "This usually indicates the ImageData buffer was detached/invalid",
      });
      return;
    }

    if (isDev) {
      const allFramesIndex = ticksCount - 1;
      this.allFrames[allFramesIndex] = blob;
    }

    // console only — per-frame. Aggregated into `scan.rotation.summary`.
    // The `Frame already exists at index` logError just below is KEPT: it is an
    // error, it is rare, and it is the signature of the scanVersion staleness
    // bug documented in HERE_BE_DRAGONS.md.
    console.debug("[ScanUploader] received frame", {
      ticksCount,
      prodFrameIndex: (ticksCount - 2) / 2,
    });
    if (ticksCount % 2 === 0) {
      const prodFrameIndex = (ticksCount - 2) / 2;
      const existingFrame = this.frames[prodFrameIndex];
      if (existingFrame) {
        logError("Frame already exists at index", {
          index: prodFrameIndex,
          existingFrame,
          ticksCount,
        });
        return;
      }
      this.frames[prodFrameIndex] = blob;
    }
    this.preUpload();
  }

  private setScanId() {
    const scanId = uuid();
    this.scanId = scanId;
    localStorage.setItem("scanID", scanId);
    localStorage.setItem("timestamp", Math.floor(Date.now() / 1000).toString());
    this.startHeartbeat();
    logInfo("[ScanUploader] setScanId()", {
      scanId,
      scanVersion: this.scanVersion,
    });
    return scanId;
  }

  /**
   * Liveness heartbeat for the scan held in localStorage.
   *
   * `localStorage.scanID` outlives this singleton: it survives a reload or a
   * tab eviction, while the 22 captured frames do not. Anything reading that
   * key later (see Utils/scanRecovery) needs to answer "is a page still
   * working on this scan?" — and the ONLY page that can answer is one that is
   * actually running. So while a scan is in flight we stamp the clock; a
   * stale stamp is positive proof that whichever page owned the scan is gone
   * and nothing can finalize it.
   *
   * This runs from setScanId() (upload start) through reset() — deliberately
   * covering the wait for the user to press Submit, which can be minutes of
   * form-filling on a live page. Backgrounded pages get their timers
   * throttled, so a stale stamp can also mean "alive but suspended"; that is
   * why the staleness threshold is generous relative to the interval and why
   * scanRecovery still refuses to touch a scan this tab's uploader owns.
   */
  private startHeartbeat() {
    this.writeHeartbeat();
    if (this.heartbeatInterval) clearInterval(this.heartbeatInterval);
    this.heartbeatInterval = setInterval(
      () => this.writeHeartbeat(),
      SCAN_HEARTBEAT_INTERVAL_MS,
    );
  }

  private writeHeartbeat() {
    try {
      localStorage.setItem(SCAN_HEARTBEAT_KEY, Date.now().toString());
    } catch {
      // Quota/private-mode failures are non-fatal: a missing stamp makes
      // scanRecovery fall back to its conservative time-based graces.
    }
  }

  private stopHeartbeat() {
    if (this.heartbeatInterval) {
      clearInterval(this.heartbeatInterval);
      this.heartbeatInterval = null;
    }
    localStorage.removeItem(SCAN_HEARTBEAT_KEY);
  }

  /**
   * Arm the stall watchdog — see UPLOAD_STALL_TIMEOUT_MS for why this exists
   * and how its duration was chosen.
   */
  private startStallWatchdog(delayMs = UPLOAD_STALL_TIMEOUT_MS) {
    this.clearStallWatchdog();
    this.stallTimeout = setTimeout(() => this.onUploadStalled(), delayMs);
  }

  private clearStallWatchdog() {
    if (this.stallTimeout) {
      clearTimeout(this.stallTimeout);
      this.stallTimeout = null;
    }
  }

  /**
   * Arm the finalize watchdog — see FINALIZE_STALL_TIMEOUT_MS. Deliberately a
   * SEPARATE timer from the upload stall watchdog rather than a relaxation of
   * onUploadStalled()'s `|| this.uploadCompleted` guard: that clause is what
   * stops the upload-phase watchdog firing after a normal completion, and
   * dropping it to cover finalize would break the phase it was written for.
   */
  private startFinalizeWatchdog(delayMs = FINALIZE_STALL_TIMEOUT_MS) {
    this.clearFinalizeWatchdog();
    this.finalizeTimeout = setTimeout(() => this.onFinalizeStalled(), delayMs);
  }

  private clearFinalizeWatchdog() {
    if (this.finalizeTimeout) {
      clearTimeout(this.finalizeTimeout);
      this.finalizeTimeout = null;
    }
  }

  /**
   * The finalize sequence neither completed nor failed. Prefer re-queueing over
   * erroring: the same shape runFinalizeScan's AbortError branch already uses,
   * and if the commit did land in the meantime the re-entry takes
   * claimAndCheckRecovery's "duplicate" branch and no-op-succeeds. Bounded by
   * MAX_FINALIZE_STALL_RETRIES so a deterministic hang cannot loop forever;
   * after that, fall back to ERROR + reset() so the user gets the existing
   * "Unable to upload to server" banner and a clean state to rescan from,
   * instead of an indefinite freeze.
   */
  private onFinalizeStalled() {
    this.finalizeTimeout = null;
    // Committed, torn down, or never started — nothing to do.
    if (!this.finalizing) return;

    const scanIsLive =
      !!this.scanId &&
      this.uploadStarted &&
      this.uploadCompleted &&
      this.scanSubmitted;

    logError("[ScanUploader] finalizeScan stalled — no completion within timeout", {
      scanId: this.scanId,
      scanVersion: this.scanVersion,
      timeoutMs: FINALIZE_STALL_TIMEOUT_MS,
      storePhotosEnabled: this.storePhotosEnabled,
      finalizeStallRetries: this.finalizeStallRetries,
      scanIsLive,
    });

    if (scanIsLive && this.finalizeStallRetries < MAX_FINALIZE_STALL_RETRIES) {
      this.finalizeStallRetries++;
      logInfo("[ScanUploader] finalizeScan stalled but scan is live — re-queuing", {
        scanId: this.scanId,
        scanVersion: this.scanVersion,
        attempt: this.finalizeStallRetries,
      });
      this.finalizing = false;
      this.finalizeScan();
      return;
    }

    // Order matters: reset() refuses to run while uploadStarted is true.
    this.uploadStarted = false;
    this.finalizing = false;
    if (this.finalizeTicker) {
      clearInterval(this.finalizeTicker);
      this.finalizeTicker = null;
    }
    // Release the claim so the doc isn't held for the full
    // SUBMITTING_TTL_SECONDS after we've given up on it.
    this.releaseSubmittingClaim();
    this.emit(
      UploadEvents.ERROR,
      new Error("Submission timed out. Please try again."),
    );
    this.reset();
  }

  /**
   * The upload never completed and never failed. Surface it the same way an
   * exhausted retry ladder does — ERROR then reset — so the user gets the
   * existing "Unable to upload to server" banner and a clean state to rescan
   * from, instead of an indefinite spinner.
   */
  private onUploadStalled() {
    this.stallTimeout = null;
    // Completed or torn down in the meantime — nothing to do.
    if (!this.uploadStarted || this.uploadCompleted) return;

    logError("[ScanUploader] upload stalled — no completion within timeout", {
      scanId: this.scanId,
      scanVersion: this.scanVersion,
      timeoutMs: UPLOAD_STALL_TIMEOUT_MS,
      docUploadProgress: this.docUploadProgress,
      scanSubmitted: this.scanSubmitted,
      storePhotosEnabled: this.storePhotosEnabled,
    });
    // Order matters: reset() refuses to run while uploadStarted is true.
    // Mirrors the exhausted-retries path above.
    this.uploadStarted = false;
    this.emit(
      UploadEvents.ERROR,
      new Error("Upload timed out. Please try again."),
    );
    this.reset();
  }

  private async callEndpoint(authedUser?: User) {
    const formData = new FormData();
    // Scan-time re-auth guard (Failure mode G): on iOS Safari / in-app
    // WKWebViews, auth.currentUser can be silently evicted to null mid-session,
    // which previously made this throw "User is not authenticated!" and wiped
    // the captured frames. When upload() threaded its captured user, reuse it so
    // /bin-predict and the upload_processing listener share one uid (and the uid
    // is stable across retries); otherwise (the finalize recovery re-run) sign in
    // ONCE here. Either way use the returned user — do NOT re-read auth.currentUser
    // below, it can stay null even after a successful anon sign-in. See
    // HERE_BE_DRAGONS.md § "Scan upload pipeline" / "Auth flow".
    const user = authedUser ?? (await ensureAuthedUser());
    const token = await user.getIdToken();
    if (!token) {
      throw new Error("User is not authenticated!");
    }
    if (this.frames.length !== 22) {
      throw new Error("Not enough frames!");
    }
    // check if all frames not null
    const allFramesValid = this.frames.every((frame) => frame !== null);
    if (!allFramesValid) {
      throw new Error("Not enough frames!");
    }
    if (this.partialStates.length !== 22) {
      throw new Error("Not enough partial states!");
    }
    const allPartialStatesValid = this.partialStates.every(
      (state) => state !== null,
    );
    if (!allPartialStatesValid) {
      throw new Error("Not all partial states are valid!");
    }
    if (!this.scanId) {
      throw new Error("Scan ID is not set!");
    }
    if (!this.gravityString) {
      throw new Error("Gravity string is not set!");
    }

    this.frames.forEach((frame, index) => {
      if (frame) formData.append("input_images", frame, `snapshot${index}.png`);
    });
    formData.append("rotation_states", JSON.stringify(this.partialStates));
    formData.append("user_id", user.uid);
    formData.append("scan_id", this.scanId);
    formData.append("gravity", this.gravityString);
    this.segmentationAbortController?.abort();
    this.segmentationAbortController = new AbortController();

    const response = await fetch(backgroundProcessingEndpoint, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${token}`,
        Accept: "*/*",
      },
      body: formData,
      signal: this.segmentationAbortController.signal,
    });

    // Read the response body to maintain the connection
    const result = await response.json();

    // Return the parsed data along with response details
    return {
      ok: response.ok,
      status: response.status,
      statusText: response.statusText,
      data: result,
    };
  }

  private async callUploadPhotosEndpoint() {
    const formData = new FormData();
    // Same scan-time re-auth guard as callEndpoint. /upload-photos runs inside
    // the finalize sequence, so a null auth.currentUser here would resurface
    // Failure mode G at Submit for photo-opt-in users. Re-auth ONCE; use the
    // credential's user — never re-read auth.currentUser. See HERE_BE_DRAGONS.md.
    const user = await ensureAuthedUser();
    const token = await user.getIdToken();
    if (!token) {
      throw new Error("User is not authenticated!");
    }
    if (this.frames.length !== 22) {
      throw new Error("Not enough frames!");
    }
    const allFramesValid = this.frames.every((frame) => frame !== null);
    if (!allFramesValid) {
      throw new Error("Not enough frames!");
    }
    if (!this.scanId) {
      throw new Error("Scan ID is not set!");
    }

    this.frames.forEach((frame, index) => {
      if (frame) formData.append("input_images", frame, `snapshot${index}.jpg`);
    });
    formData.append("user_id", user.uid);
    formData.append("scan_id", this.scanId);

    this.uploadPhotosAbortController?.abort();
    const controller = new AbortController();
    this.uploadPhotosAbortController = controller;

    // BUG-0007 2a. onForegrounded no longer cancels this request, so this
    // timeout is the ONLY thing bounding it — without it, a request suspended
    // by the OS could hang the finalize sequence indefinitely, which is the
    // hazard the foreground abort was (badly) covering. Local const, not
    // `this.uploadPhotosAbortController`: a concurrent reset()/discardScan()
    // can null or replace the field, and this timer must only ever abort the
    // request it was armed for.
    const timeoutId = setTimeout(
      () => controller.abort(),
      UPLOAD_PHOTOS_TIMEOUT_MS,
    );
    try {
      const response = await fetch(uploadPhotosEndpoint, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "*/*",
        },
        body: formData,
        signal: controller.signal,
      });

      const result = await response.json();

      return {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        data: result,
      };
    } finally {
      clearTimeout(timeoutId);
    }
  }
}
